import { DomainPruneEntities } from 'app/components/domains/types';
import { FileType } from 'data';

export const basePath = '/';

const applyBaseUrl = (url: string) => `${basePath}${url}`;

// -------------------- MONITORING ------------------- //
export const monitoringPath = applyBaseUrl('monitoring');
// Legacy redirects
export const dashboardPath = applyBaseUrl('dashboard');
export const dashboardClusterPath = `${dashboardPath}/cluster`;
// export const dashboardNodePath = `${dashboardPath}/node`;

// -------------------- DOMAINS ------------------- //
export const domainsPath = applyBaseUrl('domains');
export const domainsCreatePath = `${domainsPath}/create`;
export const domainsDetailsPath = (domainName: string) => `${domainsPath}/${domainName}`;
export const domainScheduleBackupPath = (domainName: string) => `${domainsDetailsPath(domainName)}/schedule-backups`;
export const domainBackupsHistoryPath = (domainName: string) => `${domainsDetailsPath(domainName)}/backups-history`;

// -------------------- REMOTE DOMAINS ------------------- //
export const remoteDomainsPath = applyBaseUrl('remote-domains');
export const remoteDomainsCreatePath = `${remoteDomainsPath}/create`;
export const remoteDomainsDetailsPath = (domainName: string) => `${remoteDomainsPath}/${domainName}`;

// -------------------- Applications ------------------- //
export const applicationsPath = applyBaseUrl('applications');
export const applicationCreatePath = `${applicationsPath}/create`;
export const applicationDetailsPath = (appName: string) => `${applicationsPath}/${appName}`;

// -------------------- PARTICIPANTS ------------------- //
export const participantsPath = applyBaseUrl('participants');
export const participantsCreatePath = `${participantsPath}/create`;
export const participantsDetailsPath = (participantName: string) => `${participantsPath}/${participantName}`;
export const participantScheduleBackupPath = (participantName: string) =>
  `${participantsDetailsPath(participantName)}/schedule-backups`;
export const participantBackupsHistoryPath = (participantName: string) =>
  `${participantsDetailsPath(participantName)}/backups-history`;

// -------------------- REMOTE PARTICIPANTS ------------------- //
export const remoteParticipantsPath = applyBaseUrl('remote-participants');
export const remoteParticipantsCreatePath = `${remoteParticipantsPath}/create`;
export const remoteParticipantsDetailsPath = (participantName: string) =>
  `${remoteParticipantsPath}/${participantName}`;
export const baseParticipantDetailsPath = (participantName: string, isRemote: boolean) =>
  isRemote ? remoteParticipantsDetailsPath(participantName) : participantsDetailsPath(participantName);

// -------------------- ENTITIES list view paths --------------------
export const EDIT_LIST_PATH = 'edit-list';
export const applicationEditPathFromList = (appName: string) => `${applicationDetailsPath(appName)}/${EDIT_LIST_PATH}`;
export const domainEditPathFromList = (domainName: string) => `${domainsDetailsPath(domainName)}/${EDIT_LIST_PATH}`;
export const remoteDomainEditPathFromList = (domainName: string) =>
  `${remoteDomainsDetailsPath(domainName)}/${EDIT_LIST_PATH}`;
export const remoteParticipantEditPathFromList = (remoteParticipantName: string) =>
  `${remoteParticipantsDetailsPath(remoteParticipantName)}/${EDIT_LIST_PATH}`;
export const participantConnectPathFromList = (participantName: string) =>
  `${participantsDetailsPath(participantName)}/connect-list`;
export const participantEditPathFromList = (participantName: string) =>
  `${participantsDetailsPath(participantName)}/${EDIT_LIST_PATH}`;
export const participantUploadDarPathFromList = (participantName: string) =>
  `${participantsDetailsPath(participantName)}/upload-dar-list`;
export const remoteParticipantUploadDarPathFromList = (participantName: string) =>
  `${remoteParticipantsDetailsPath(participantName)}/upload-dar-list`;
export const remoteParticipantConnectPathFromList = (participantName: string) =>
  `${remoteParticipantsDetailsPath(participantName)}/connect-list`;

// -------------------- ENTITIES details view page --------------------
export const applicationEditPath = (appName: string) => `${applicationDetailsPath(appName)}/edit`;
export const domainEditPath = (domainName: string) => `${domainsDetailsPath(domainName)}/edit`;
export const remoteDomainEditPath = (domainName: string) => `${remoteDomainsDetailsPath(domainName)}/edit`;
export const participantEditPath = (participantName: string) => `${participantsDetailsPath(participantName)}/edit`;
export const remoteParticipantEditPath = (remoteParticipantName: string) =>
  `${remoteParticipantsDetailsPath(remoteParticipantName)}/edit`;
export const participantUploadDarPath = (participantName: string) =>
  `${participantsDetailsPath(participantName)}/upload-dar`;
export const participantConnectPath = (participantName: string) =>
  `${participantsDetailsPath(participantName)}/connect`;
export const participantPruningPath = (participantName: string) =>
  `${participantsDetailsPath(participantName)}/pruning`;
export const domainPruningPath = (domainName: string, component?: DomainPruneEntities) => {
  const basePruningPath = `${domainsDetailsPath(domainName)}/pruning`;
  return !component ? basePruningPath : `${basePruningPath}?component=${component}`;
};
export const remoteDomainPruningPath = (domainName: string, component?: DomainPruneEntities) => {
  const basePruningPath = `${remoteDomainsDetailsPath(domainName)}/pruning`;
  return !component ? basePruningPath : `${basePruningPath}?component=${component}`;
};
export const remoteParticipantUploadDarPath = (participantName: string) =>
  `${remoteParticipantsDetailsPath(participantName)}/upload-dar`;
export const remoteParticipantConnectPath = (participantName: string) =>
  `${remoteParticipantsDetailsPath(participantName)}/connect`;
export const remoteParticipantPruningPath = (participantName: string) =>
  `${remoteParticipantsDetailsPath(participantName)}/pruning`;

// -------------------- PARTIES -------------------- //
export const partyCreatePath = (participantName: string, isRemote: boolean) =>
  `${baseParticipantDetailsPath(participantName, isRemote)}/create-party`;

// -------------------- USERS -------------------- //
export const userCreatePath = (participantName: string, isRemote: boolean) =>
  `${baseParticipantDetailsPath(participantName, isRemote)}/create-user`;
export const userManageRightsPath = (participantName: string, userName: string, isRemote = false) =>
  `${baseParticipantDetailsPath(participantName, isRemote)}/users/${userName}/manage-rights`;

// -------------------- COLLECTIONS ------------------- //
export const baseCollectionsPath = applyBaseUrl('collections');
export const collectionsPath = (fileType: FileType = FileType.DAR) => `${baseCollectionsPath}/${fileType}`;
export const fileUploadPath = (fileType: FileType = FileType.DAR) => `${collectionsPath(fileType)}/upload`;
export const fileDeployPath = (fileName: string, fileType: FileType = FileType.DAR) =>
  `${collectionsPath(fileType)}/${fileName}/deploy`;

// -------------------- VALIDATORS ------------------- //
export const validatorsPath = applyBaseUrl('validators');
export const validatorCreatePath = `${validatorsPath}/create`;
export const validatorDetailsPath = (validatorName: string) => `${validatorsPath}/${validatorName}`;
export const validatorEditPathFromList = (validatorName: string) => `${validatorDetailsPath(validatorName)}/edit-list`;
export const validatorEditPath = (validatorName: string) => `${validatorDetailsPath(validatorName)}/edit`;
export const validatorScheduleBackupPath = (validatorName: string) =>
  `${validatorDetailsPath(validatorName)}/schedule-backups`;
export const validatorScheduleIdDumpsPath = (validatorName: string) =>
  `${validatorDetailsPath(validatorName)}/schedule-id-dumps`;
export const validatorBackupsHistoryPath = (validatorName: string) =>
  `${validatorDetailsPath(validatorName)}/backups-history`;
export const validatorIdDumpsHistoryPath = (validatorName: string) =>
  `${validatorDetailsPath(validatorName)}/id-dumps-history`;
export const validatorPruningPath = (validatorName: string) => `${validatorDetailsPath(validatorName)}/pruning`;
export const validatorChangeWalletUserPath = (validatorName: string) =>
  `${validatorDetailsPath(validatorName)}/change-wallet-user`;

export const notFoundPath = applyBaseUrl('not-found');
